import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { getToken } from '../utils/auth';

import './ProductList.css';
import categoryVin from '../assets/verres-vin.webp';
import categoryChampagne from '../assets/cham.jpg';
import categoryWhisky from '../assets/category-whisky.png';
import categoryBoissons from '../assets/free.jpg';

const API_BASE = 'http://localhost:5001';
const notifyCartUpdated = () => window.dispatchEvent(new CustomEvent('cart:updated'));

const CATEGORY_DEFINITIONS = [
  { label: 'Vin', slug: 'vin' },
  { label: 'Champagne', slug: 'champagne' },
  { label: 'Whisky', slug: 'whisky' },
  { label: 'Boissons non alcoolisées', slug: 'autre-boisson-alcoolisee' }
];

const CATEGORY_IMAGES = {
  vin: categoryVin,
  champagne: categoryChampagne,
  whisky: categoryWhisky,
  'autre-boisson-alcoolisee': categoryBoissons
};

const CATEGORY_DISPLAY_LABELS = {
  'autre-boisson-alcoolisee': 'Boissons sans alcool'
};

function ProductList() {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addError, setAddError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(() => location.state?.selectedCategory ?? null);
  const stateSelectedCategory = location.state?.selectedCategory;
  const [previewImage, setPreviewImage] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getToken());
  const [quantityModalProduct, setQuantityModalProduct] = useState(null);
  const [quantityInput, setQuantityInput] = useState('1');
  const [quantityModalError, setQuantityModalError] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/products`);
        setProducts(res.data);
      } catch (err) {
        setError('Erreur lors du chargement des produits');
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const syncAuth = () => setIsAuthenticated(!!getToken());
    syncAuth();
    window.addEventListener('storage', syncAuth);

    return () => window.removeEventListener('storage', syncAuth);
  }, []);

  const getImageUrl = (img) => {
    if (!img) return null;
    if (img.startsWith('http')) return img;
    const normalized = img.startsWith('/') ? img : `/${img}`;
    return `${API_BASE}${normalized}`;
  };

  useEffect(() => {
    if (typeof stateSelectedCategory !== 'undefined' && stateSelectedCategory !== selectedCategory) {
      setSelectedCategory(stateSelectedCategory);
    }
  }, [stateSelectedCategory, selectedCategory]);

  const handleViewDetails = (product) => {
    navigate(`/products/${product._id}`, { state: { fromProductList: true, selectedCategory, searchParams: location.search } });
  };

  const openQuantityModal = (product) => {
    if (!product) return;
    setQuantityModalProduct(product);
    setQuantityInput('1');
    setQuantityModalError('');
  };

  const closeQuantityModal = () => {
    setQuantityModalProduct(null);
    setQuantityModalError('');
  };

  const performAddToCart = async (product, quantity) => {
    setSuccess('');
    setAddError('');

    const authToken = localStorage.getItem('token');
    if (!authToken) {
      setAddError('Veuillez vous connecter pour ajouter au panier.');
      closeQuantityModal();
      navigate('/login');
      return;
    }

    let safeQuantity = Math.max(1, Number(quantity) || 1);

    const rawAvailable = product.stock ?? product.quantity;
    const available = (() => {
      if (rawAvailable === undefined || rawAvailable === null || rawAvailable === '') return null;
      const numeric = Number(rawAvailable);
      return Number.isFinite(numeric) ? numeric : null;
    })();

    if (available !== null) {
      if (available <= 0) {
        setAddError('Ce produit est en rupture de stock.');
        return;
      }
      if (safeQuantity > available) {
        safeQuantity = available;
      }
    }

    try {
      await axios.post(`${API_BASE}/api/orders`, {
        productId: product._id,
        quantity: safeQuantity
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setSuccess(`Ajouté au panier : ${product.name} x ${safeQuantity}`);
      notifyCartUpdated();
      closeQuantityModal();
    } catch (err) {
      const message = err.response?.data?.error || "Erreur lors de l'ajout au panier";
      setAddError(message);
    }
  };

  const normalizedProducts = products.map((product) => ({
    ...product,
    normalizedCategory: (product.category || '').toString().trim().toLowerCase(),
    description: product.description || ''
  }));

  const searchTerm = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('search')?.trim().toLowerCase() || '';
  }, [location.search]);

  const filteredProducts = useMemo(() => {
    if (!searchTerm) return normalizedProducts;
    return normalizedProducts.filter((product) => {
      const name = (product.name || '').toString().toLowerCase();
      const description = (product.description || '').toString().toLowerCase();
      const category = (product.category || '').toString().toLowerCase();
      return (
        name.includes(searchTerm) ||
        description.includes(searchTerm) ||
        category.includes(searchTerm)
      );
    });
  }, [normalizedProducts, searchTerm]);

  const categorizedProducts = CATEGORY_DEFINITIONS.map((category) => ({
    ...category,
    products: filteredProducts.filter((product) => product.normalizedCategory === category.slug)
  }));

  const uncategorizedProducts = filteredProducts.filter(
    (product) => !CATEGORY_DEFINITIONS.some((category) => product.normalizedCategory === category.slug)
  );

  const isFiltering = !!searchTerm;
  const availableCategories = categorizedProducts.filter((category) => category.products.length > 0);
  const uncategorizedEntry = uncategorizedProducts.length > 0 ? [{
    slug: 'uncategorized',
    label: 'Autres produits',
    products: uncategorizedProducts
  }] : [];
  const displayableCategories = [...availableCategories, ...uncategorizedEntry];
  const handleCategorySelect = (slug) => {
    setSelectedCategory((prev) => (prev === slug ? null : slug));
  };
  const selectedCategoryData = displayableCategories.find((category) => category.slug === selectedCategory);
  const selectedProducts = selectedCategoryData ? selectedCategoryData.products : [];
  const searchResultsCount = filteredProducts.length;
  const isSearchEmpty = isFiltering && searchResultsCount === 0;
  const isBoldCategory = (slug) => slug === 'autre-boisson-alcoolisee';
  const resolveCategoryLabel = (slug, fallback) => CATEGORY_DISPLAY_LABELS[slug] || fallback;

  const handleImagePreview = (src, alt) => {
    setPreviewImage({ src, alt });
  };

  const closePreview = () => setPreviewImage(null);

  const renderProductCard = (product) => {
    const imageUrl = getImageUrl(product.image);
    const categoryLabel =
      product.category?.name ||
      product.category ||
      product.variety ||
      'Sélection exclusive';

    const packQuantityDisplay = (() => {
      if (product.packQuantity === null || product.packQuantity === undefined) return '';
      if (typeof product.packQuantity === 'number' && Number.isFinite(product.packQuantity)) {
        return product.packQuantity.toString();
      }
      return product.packQuantity;
    })();

    const packInfoParts = [
      packQuantityDisplay,
      (product.volume || '').trim(),
      (product.alcoholPercentage || '').trim(),
      (product.countryCode || '').trim()
    ].filter(Boolean);

    const metaIconMap = {
      variety: '🍷',
      pack: '📦',
      barcode: '🔖',
      weight: '⚖️',
      pallet: '🏷️'
    };

    const metaItems = [
      product.variety ? { type: 'variety', value: product.variety } : null,
      packInfoParts.length ? { type: 'pack', value: packInfoParts.join(' / ') } : null,
      product.barcode ? { type: 'barcode', value: product.barcode } : null,
      product.weight ? { type: 'weight', value: product.weight } : null,
      product.pallet ? { type: 'pallet', value: product.pallet } : null
    ].filter(Boolean);

    const formattedPrice =
      typeof product.price === 'number' && Number.isFinite(product.price)
        ? `${product.price.toFixed(2)} €`
        : product.price || '';

    return (
      <article key={product._id} className="product-card">
        <div className="product-card__media">
          {imageUrl ? (
            <button
              type="button"
              className="product-card__media-trigger"
              onClick={() => handleImagePreview(imageUrl, product.name)}
            >
              <img src={imageUrl} alt={product.name} className="product-card__media-image" />
            </button>
          ) : (
            <div className="product-card__media-placeholder">Aucune image</div>
          )}
          <span className="product-card__badge">{categoryLabel}</span>
        </div>

        <div className="product-card__content">
          <div className="product-card__body">
            <div className="product-card__header">
              <h4 className="product-card__name">{product.name}</h4>
              {packInfoParts.length > 0 && (
                <p className="product-card__caption">{packInfoParts.join(' • ')}</p>
              )}
            </div>

            {metaItems.length > 0 && (
              <ul className="product-card__meta product-card__meta--chips">
                {metaItems.map((item, index) => (
                  <li
                    key={`${product._id}-meta-${item.type}-${index}`}
                    className={`product-card__chip product-card__chip--${item.type}`}
                  >
                    <span className="product-card__chip-icon" aria-hidden="true">
                      {metaIconMap[item.type] || '•'}
                    </span>
                    <span>{item.value}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="product-card__footer">
            <div className="product-card__price-block">
              <span className={`product-card__price${isAuthenticated ? '' : ' product-card__price--locked'}`}>
                {isAuthenticated ? formattedPrice || 'Prix disponible' : 'Connectez-vous pour voir les prix'}
              </span>
              {product.stock !== undefined && product.stock !== null && (
                <span className="product-card__stock">Stock : {product.stock}</span>
              )}
            </div>

            <div className="product-card__cta-row">
              <button
                onClick={() => handleViewDetails(product)}
                className="product-card__button product-card__button--ghost"
              >
                Voir détail
              </button>
              <button onClick={() => openQuantityModal(product)} className="product-card__button">
                Ajouter
              </button>
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="product-list">
      <h2 className="product-list__title">Liste des produits</h2>
      {loading ? (
        <p>Chargement...</p>
      ) : error ? (
        <p className="product-list__message product-list__message--error">{error}</p>
      ) : (
        <>
          {success && <p className="product-list__message product-list__message--success">{success}</p>}
          {addError && <p className="product-list__message product-list__message--error">{addError}</p>}
          {isFiltering ? (
            isSearchEmpty ? (
              <p className="product-list__message">Aucun produit ne correspond à votre recherche.</p>
            ) : (
              <div className="product-search-results">
                <div className="product-search-results__header">
                  <h3>Résultats de recherche</h3>
                  <span>{searchResultsCount} produit{searchResultsCount > 1 ? 's' : ''}</span>
                </div>
                <div className="product-grid">
                  {filteredProducts.map((product) => renderProductCard(product))}
                </div>
              </div>
            )
          ) : displayableCategories.length === 0 ? (
            <p className="product-list__message">Aucun produit n'est disponible pour le moment.</p>
          ) : (
            <>
              <div className="product-category-menu">
                {displayableCategories.map((category) => {
                  const isActive = selectedCategory === category.slug;
                  const illustration = CATEGORY_IMAGES[category.slug];
                  return (
                    <div key={category.slug} className="product-category-menu__item">
                      <button
                        type="button"
                        className={`product-category-menu__button${isActive ? ' product-category-menu__button--active' : ''}`}
                        onClick={() => handleCategorySelect(category.slug)}
                      >
                        {illustration ? (
                          <img src={illustration} alt={category.label} className="product-category-menu__image" />
                        ) : (
                          <div className="product-category-menu__image product-category-menu__image--placeholder">{category.label}</div>
                        )}
                      </button>
                      {isBoldCategory(category.slug) ? (
                        <strong className="product-category-menu__label product-category-menu__label--bold">{resolveCategoryLabel(category.slug, category.label)}</strong>
                      ) : (
                        <span className="product-category-menu__label">{resolveCategoryLabel(category.slug, category.label)}</span>
                      )}
                      <span className="product-category-menu__count">{category.products.length} PRODUIT(S)</span>
                    </div>
                  );
                })}
              </div>
              {selectedCategoryData ? (
                <section className="product-category">
                  <header className="product-category__header">
                    {isBoldCategory(selectedCategoryData.slug) ? (
                      <h3 className="product-category__title product-category__title--bold">{resolveCategoryLabel(selectedCategoryData.slug, selectedCategoryData.label)}</h3>
                    ) : (
                      <h3 className="product-category__title">{resolveCategoryLabel(selectedCategoryData.slug, selectedCategoryData.label)}</h3>
                    )}
                    <span className="product-category__count">{selectedProducts.length} PRODUIT(S)</span>
                  </header>
                  <div className="product-grid">
                    {selectedProducts.map((product) => renderProductCard(product))}
                  </div>
                </section>
              ) : (
                <p className="product-list__message">Sélectionnez une catégorie pour afficher ses produits.</p>
              )}
            </>
          )}
        </>
      )}
      {previewImage && (
        <div className="product-image-preview" role="dialog" aria-modal="true" onClick={closePreview}>
          <div className="product-image-preview__content" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="product-image-preview__close" onClick={closePreview} aria-label="Fermer l'aperçu de l'image">×</button>
            <img src={previewImage.src} alt={previewImage.alt} className="product-image-preview__image" />
          </div>
        </div>
      )}
      {quantityModalProduct && (
        <div className="product-quantity-modal" role="dialog" aria-modal="true">
          <div className="product-quantity-modal__content">
            <button type="button" className="product-quantity-modal__close" onClick={closeQuantityModal} aria-label="Fermer la saisie de quantité">×</button>
            <h3 className="product-quantity-modal__title">Ajouter au panier</h3>
            <p className="product-quantity-modal__subtitle">{quantityModalProduct.name}</p>
            <label className="product-quantity-modal__label" htmlFor="quantity-input">Quantité souhaitée</label>
            <input
              id="quantity-input"
              type="number"
              min="1"
              className="product-quantity-modal__input"
              value={quantityInput}
              onChange={(event) => {
                setQuantityInput(event.target.value);
                setQuantityModalError('');
              }}
            />
            {quantityModalError && (
              <p className="product-quantity-modal__error">{quantityModalError}</p>
            )}
            <div className="product-quantity-modal__actions">
              <button type="button" className="product-quantity-modal__button product-quantity-modal__button--ghost" onClick={closeQuantityModal}>Annuler</button>
              <button
                type="button"
                className="product-quantity-modal__button"
                onClick={() => {
                  const parsed = Math.floor(Number(quantityInput));
                  if (!Number.isFinite(parsed) || parsed <= 0) {
                    setQuantityModalError('Veuillez saisir une quantité valide (minimum 1).');
                    return;
                  }
                  performAddToCart(quantityModalProduct, parsed);
                }}
              >
                Ajouter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


export default ProductList;
